import React, { useEffect, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useNavigate } from "react-router-dom";
import { X, ArrowLeft } from "lucide-react";
import { pageURLs } from "@/constants";
import {
  getCloseOutContext,
  getNextBuyerDraft,
  submitCloseOut,
} from "@/services/closeOutLoopService";
import CloseOutOutcomeList from "@/components/CloseOutOutcomeList";
import {
  FoundStep,
  DetailsStep,
  digitsOnly,
} from "@/components/CloseOutDetails";
import "./CloseOutSheet.scss";

// Only the reversible outcome saves on one tap; ending the buyer relationship
// is terminal on the server, so it goes through a confirm step.
const ONE_TAP = ["paused"];
const FINAL_STATUSES = ["CLOSED", "TERMINATED"];

const CloseOutSheet = ({
  show,
  wantAd,
  onHide,
  onRemoveWithoutOutcome,
  onClosedOut,
}) => {
  const navigate = useNavigate();
  const wantAdId = wantAd?.wantAdId;
  const [step, setStep] = useState("choose");
  const [accepted, setAccepted] = useState([]);
  const [contextState, setContextState] = useState("idle");
  const [reason, setReason] = useState(null);
  const [introductionId, setIntroductionId] = useState("");
  const [details, setDetails] = useState({});
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [contextNonce, setContextNonce] = useState(0);
  const firstRowRef = useRef(null);

  useEffect(() => {
    if (!show || !wantAdId) return;
    setStep("choose");
    setReason(null);
    setIntroductionId("");
    setDetails({});
    setBusy(null);
    setError(null);
    setResult(null);
  }, [show, wantAdId]);

  useEffect(() => {
    if (!show || !wantAdId) return undefined;
    let live = true;
    setAccepted([]);
    setContextState("loading");
    getCloseOutContext(wantAdId)
      .then((data) => {
        if (!live) return;
        const list = data?.acceptedIntroductions || [];
        setAccepted(list);
        if (list.length === 1)
          setIntroductionId(String(list[0].introductionId));
        setContextState("ready");
      })
      .catch(() => live && setContextState("error"));
    return () => {
      live = false;
    };
  }, [show, wantAdId, contextNonce]);

  // "Closed" can be tapped before the context loads (or after a failed load
  // and retry); default to crediting the BLS introduction once it is known.
  useEffect(() => {
    if (step === "found" && !reason && contextState === "ready") {
      setReason(accepted.length ? "found_via_bls" : "found_via_mls");
    }
  }, [step, reason, contextState, accepted]);

  const close = () => {
    if (busy) return;
    onHide && onHide();
  };

  const isAlreadyFinal = async () => {
    try {
      const data = await getCloseOutContext(wantAdId);
      return FINAL_STATUSES.includes(data?.status);
    } catch {
      return false;
    }
  };

  const submit = async (chosen, withDetails) => {
    setBusy(chosen);
    setError(null);
    try {
      const payload = { reason: chosen, details: withDetails ? details : {} };
      if (chosen === "found_via_bls") payload.introductionId = introductionId;
      if (payload.details.purchasePrice) {
        payload.details = {
          ...payload.details,
          purchasePrice: digitsOnly(payload.details.purchasePrice),
        };
      }
      const saved = await submitCloseOut(wantAdId, payload);
      setResult(saved);
      onClosedOut && onClosedOut(saved);
      setBusy(null);
      if (saved?.canStartNextBuyer) setStep("done");
      else onHide && onHide();
    } catch (e) {
      // A timed-out save can still have committed; a retry then returns 409.
      // If the listing is already final, treat it as done instead of stranding
      // the agent on an error with a stale list behind the sheet.
      if (e.code === 409 && (await isAlreadyFinal())) {
        onClosedOut && onClosedOut(null);
        setBusy(null);
        onHide && onHide();
        return;
      }
      setError(e.message);
      setBusy(null);
    }
  };

  const pick = (outcome) => {
    setError(null);
    if (ONE_TAP.includes(outcome)) return submit(outcome, false);
    if (outcome === "not_working_with_buyer") {
      setReason(outcome);
      setStep("confirm");
    } else if (outcome === "found") {
      // Left unset until the context is ready; the effect above defaults it.
      setReason(
        contextState !== "ready"
          ? null
          : accepted.length
            ? "found_via_bls"
            : "found_via_mls",
      );
      setStep("found");
    } else {
      setReason("under_contract");
      setStep("details");
    }
    return null;
  };

  const startNextBuyer = async () => {
    setBusy("next");
    setError(null);
    try {
      const { draft } = await getNextBuyerDraft(wantAdId);
      setBusy(null);
      onHide && onHide();
      navigate(pageURLs.agentCreatewantad, { state: { data: draft } });
    } catch (e) {
      setError(e.message);
      setBusy(null);
    }
  };

  const back = () => {
    setError(null);
    setStep(
      step === "details" && reason !== "under_contract" ? "found" : "choose",
    );
  };

  return (
    <Modal
      show={show}
      onHide={close}
      centered
      dialogClassName="close-out-sheet"
      aria-labelledby="close-out-title"
      backdrop={busy ? "static" : true}
      keyboard={!busy}
      onEntered={() => firstRowRef.current && firstRowRef.current.focus()}
    >
      <Modal.Body>
        <div className="close-out-bar">
          {step === "found" || step === "details" || step === "confirm" ? (
            <button
              type="button"
              className="close-out-icon-button"
              aria-label="Back"
              onClick={back}
              disabled={!!busy}
            >
              <ArrowLeft size={18} />
            </button>
          ) : (
            <span />
          )}
          <button
            type="button"
            className="close-out-icon-button"
            aria-label="Close"
            onClick={close}
          >
            <X size={18} />
          </button>
        </div>
        {wantAd?.title && step !== "done" && (
          <p className="close-out-listing">{wantAd.title}</p>
        )}
        {error && (
          <p className="close-out-error" role="alert">
            {error}
          </p>
        )}
        {step === "choose" && (
          <>
            <h2 className="close-out-title" id="close-out-title">
              Update status
            </h2>
            <CloseOutOutcomeList
              onPick={pick}
              onRemove={() =>
                onRemoveWithoutOutcome && onRemoveWithoutOutcome(wantAdId)
              }
              busy={busy}
              firstRowRef={firstRowRef}
            />
          </>
        )}
        {step === "found" && (
          <FoundStep
            reason={reason}
            onReason={setReason}
            accepted={accepted}
            contextState={contextState}
            introductionId={introductionId}
            onIntroduction={setIntroductionId}
            onContinue={() => setStep("details")}
            onRetry={() => setContextNonce((n) => n + 1)}
          />
        )}
        {step === "confirm" && (
          <>
            <h2 className="close-out-title" id="close-out-title">
              No longer working with this buyer?
            </h2>
            <p className="close-out-listing">
              This ends the Want-Listing and can't be undone.
            </p>
            <div className="close-out-actions">
              <button
                type="button"
                className="btn btn-primary close-out-primary"
                onClick={() => submit(reason, false)}
                disabled={!!busy}
              >
                {busy ? "Saving..." : "Yes, remove it"}
              </button>
              <button
                type="button"
                className="close-out-text-button"
                onClick={back}
                disabled={!!busy}
              >
                Cancel
              </button>
            </div>
          </>
        )}
        {step === "details" && (
          <DetailsStep
            reason={reason}
            details={details}
            onChange={setDetails}
            onSave={() => submit(reason, true)}
            onSkip={() => submit(reason, false)}
            busy={busy}
          />
        )}
        {step === "done" && (
          <>
            <h2 className="close-out-title" id="close-out-title">
              {result?.reason === "under_contract"
                ? "Under Contract"
                : "Closed"}
            </h2>
            <div className="close-out-actions">
              <button
                type="button"
                className="btn btn-primary close-out-primary"
                onClick={startNextBuyer}
                disabled={busy === "next"}
              >
                {busy === "next"
                  ? "Opening the draft..."
                  : "Start your next buyer from this one"}
              </button>
              <button
                type="button"
                className="close-out-text-button"
                onClick={close}
              >
                Done
              </button>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default CloseOutSheet;
